(() => {
  if (window.AscProductCards) {
    window.AscProductCards.scan(document);
    return;
  }

  const snapScrollbarToMedia = (gallery) => {
    const scrollbar = gallery.closest('.asc-catalog-card')?.querySelector('[data-mobile-card-scrollbar]');
    if (!scrollbar) return;

    const mediaBottom = gallery.getBoundingClientRect().bottom;
    const pixelRatio = window.devicePixelRatio || 1;
    const snappedBottom = Math.ceil((mediaBottom * pixelRatio) - 0.0001) / pixelRatio;
    scrollbar.style.setProperty('--asc-card-progress-shift', `${snappedBottom - mediaBottom}px`);
  };

  const visibleGalleries = new Set();
  const galleryVisibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleGalleries.add(entry.target);
            snapScrollbarToMedia(entry.target);
          } else {
            visibleGalleries.delete(entry.target);
          }
        });
      })
    : null;

  const initializeGallery = (gallery) => {
    if (gallery.dataset.mobileGalleryReady === 'true') return;
    delete gallery.dataset.mobileGalleryPending;

    const track = gallery.querySelector('[data-mobile-card-gallery-track]');
    const deferredSlides = track?.querySelector('[data-mobile-card-gallery-template]');
    if (deferredSlides) {
      track.appendChild(deferredSlides.content.cloneNode(true));
      deferredSlides.remove();
    }

    const deferredData = track?.querySelector('[data-mobile-card-gallery-data]');
    if (deferredData) {
      try {
        const fragment = document.createDocumentFragment();
        const items = JSON.parse(deferredData.textContent || '[]');

        items.forEach((item) => {
          const link = document.createElement('a');
          const image = document.createElement('img');
          const imageUrl = new URL(item.src, document.baseURI);
          const imageAtWidth = (width) => {
            const url = new URL(imageUrl);
            url.searchParams.set('width', width);
            return url.href;
          };

          link.className = 'asc-catalog-card__mobile-slide';
          link.href = item.href;
          link.setAttribute('aria-label', item.label);
          link.setAttribute('data-mobile-card-slide', '');

          image.className = 'asc-catalog-card__mobile-image';
          image.src = imageAtWidth(720);
          image.srcset = `${imageAtWidth(360)} 360w, ${imageAtWidth(540)} 540w, ${imageAtWidth(720)} 720w`;
          image.sizes = '(max-width: 749px) 50vw, 360px';
          image.alt = item.alt;
          image.width = item.width;
          image.height = item.height;
          image.loading = 'lazy';

          link.appendChild(image);
          fragment.appendChild(link);
        });

        track.appendChild(fragment);
      } catch {}

      deferredData.remove();
    }

    const slides = Array.from(gallery.querySelectorAll('[data-mobile-card-slide]'));
    const card = gallery.closest('.asc-catalog-card');
    const scrollbar = card?.querySelector('[data-mobile-card-scrollbar]');
    const scrollbarThumb = scrollbar?.querySelector('[data-mobile-card-scrollbar-thumb]');
    if (!track || !slides.length) return;

    gallery.dataset.mobileGalleryReady = 'true';
    track.classList.add('swiper-no-swiping');
    let activeIndex = 0;
    let scrollFrame = null;
    let pointerStartX = 0;
    let blockNextLink = false;

    const preloadAround = (index) => {
      [index, index + 1, index + 2].forEach((slideIndex) => {
        const image = slides[slideIndex]?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decoding = 'async';
        if (slideIndex <= index + 1) image.fetchPriority = 'high';
      });
    };

    const syncGallery = () => {
      scrollFrame = null;
      snapScrollbarToMedia(gallery);
      const slideWidth = track.clientWidth;
      if (!slideWidth) return;
      activeIndex = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / slideWidth)));
      preloadAround(activeIndex);

      if (scrollbar && scrollbarThumb) {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const maxThumbOffset = Math.max(0, scrollbar.clientWidth - scrollbarThumb.offsetWidth);
        const progress = maxScroll > 0 ? Math.min(1, Math.max(0, track.scrollLeft / maxScroll)) : 0;
        scrollbarThumb.style.transform = `translate3d(${progress * maxThumbOffset}px, 0, 0)`;
      }
    };

    track.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(syncGallery);
    }, { passive: true });

    const stopParentSwipe = (event) => event.stopPropagation();
    ['touchstart', 'touchmove', 'touchend', 'pointerdown', 'pointermove', 'pointerup', 'pointercancel'].forEach((eventName) => {
      track.addEventListener(eventName, stopParentSwipe, { passive: true });
    });

    track.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
      blockNextLink = false;
    }, { passive: true });

    track.addEventListener('pointermove', (event) => {
      if (Math.abs(event.clientX - pointerStartX) > 7) blockNextLink = true;
    }, { passive: true });

    track.addEventListener('click', (event) => {
      if (!blockNextLink) return;
      event.preventDefault();
      event.stopPropagation();
      blockNextLink = false;
    }, true);

    if ('ResizeObserver' in window) {
      const galleryObserver = new ResizeObserver(() => {
        if (!track.clientWidth) return;
        track.scrollLeft = activeIndex * track.clientWidth;
        syncGallery();
      });
      galleryObserver.observe(track);
      gallery._ascGalleryResizeObserver = galleryObserver;
    }

    preloadAround(0);
    syncGallery();
  };

  const prepareGallery = (gallery) => {
    if (
      gallery.dataset.mobileGalleryReady === 'true' ||
      gallery.dataset.mobileGalleryPending === 'true'
    ) return;

    gallery.dataset.mobileGalleryPending = 'true';
    snapScrollbarToMedia(gallery);
    galleryVisibilityObserver?.observe(gallery);
  };

  const scan = (root) => {
    if (root.matches?.('[data-mobile-card-gallery]')) prepareGallery(root);
    root.querySelectorAll?.('[data-mobile-card-gallery]').forEach(prepareGallery);
  };

  window.AscProductCards = { scan };
  scan(document);

  let resizeFrame = null;
  window.addEventListener('resize', () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      document.querySelectorAll('[data-mobile-card-gallery]').forEach(snapScrollbarToMedia);
    });
  }, { passive: true });

  let scrollSnapFrame = null;
  window.addEventListener('scroll', () => {
    if (scrollSnapFrame) return;
    scrollSnapFrame = requestAnimationFrame(() => {
      scrollSnapFrame = null;
      visibleGalleries.forEach(snapScrollbarToMedia);
    });
  }, { passive: true });

  const snapAllGalleries = () => {
    document.querySelectorAll('[data-mobile-card-gallery]').forEach(snapScrollbarToMedia);
  };

  if (document.readyState === 'complete') {
    requestAnimationFrame(snapAllGalleries);
  } else {
    window.addEventListener('load', snapAllGalleries, { once: true });
  }
  document.fonts?.ready.then(snapAllGalleries);

  const initializeFromEvent = (event) => {
    const gallery = event.target.closest?.('[data-mobile-card-gallery]');
    if (gallery) initializeGallery(gallery);
  };

  document.addEventListener('pointerover', initializeFromEvent, { passive: true });
  document.addEventListener('pointerdown', initializeFromEvent, { passive: true });
  document.addEventListener('touchstart', initializeFromEvent, { passive: true });
  document.addEventListener('focusin', initializeFromEvent);

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-mobile-card-gallery]').forEach((gallery) => {
      galleryVisibilityObserver?.unobserve(gallery);
      visibleGalleries.delete(gallery);
      gallery._ascGalleryResizeObserver?.disconnect();
      delete gallery._ascGalleryResizeObserver;
    });
  });
})();
